import turf from "../../../../shared/utils/turf";
import { importHlaMapConfig } from "../config";
import { importHlaUtils } from "../utils";
import { HlaWayBuilder } from "../models/HlaWayBuilder";

const layerName = importHlaMapConfig.osmLineLayer.id;

export const findAndRegisterOsmData = (
  builder: HlaWayBuilder,
  map: mapboxgl.Map,
) => {
  const lineString = turf.lineString(builder.coordinates);
  const bufferPolygon = turf.buffer(lineString, 1, {
    units: "meters",
  })!;
  const bbox = importHlaUtils.polygonToBbox(bufferPolygon, map);

  const featureIds = new Set();

  const candidates = importHlaUtils
    .flattenLineAndMultiLineFeatures(
      map.queryRenderedFeatures(bbox, {
        layers: [layerName],
      }),
    )
    .filter((feature) => turf.booleanIntersects(feature, bufferPolygon))
    .filter((f) => {
      // remove duplicates
      if (featureIds.has(f.id)) return false;
      featureIds.add(f.id);

      const matches = f.geometry.coordinates.filter((position) => {
        const distances = turf.nearestPointOnLine(lineString, position, {
          units: "meters",
        });
        return distances.properties.pointDistance < 1;
      });

      return matches.length >= 2;
    });

  if (candidates.length === 0) return;

  const matchedSegment = candidates[0];

  applyOsmTags(builder, matchedSegment.properties as Record<string, string>);
};

const applyOsmTags = (b: HlaWayBuilder, tags: Record<string, string>) => {
  console.log("Applying tags", tags);

  Object.entries(tags).forEach(([key, value]: [string, string]) => {
    const match = (str: string) => str === `${key}=${value}`;

    if (key === "maxspeed") {
      const [numberStr] = value.split(" ");
      const number = parseInt(numberStr);
      if (!isNaN(parseInt(numberStr))) {
        b.maxSpeed = number;
      }
    }

    if (key === "lit" && value === "no") {
      b.lit = false;
    }

    if (key === "width") {
      const number = parseInt(value);
      if (!isNaN(number)) {
        b.roadWidth = number;
      }
    }

    if (key === "cycleway:width") {
      const number = parseInt(value);

      if (!isNaN(number)) {
        b.cycleWayWidth = number;
      }
    }

    if (key === "lanes") {
      const [numberStr] = value.split(" ");
      const number = parseInt(numberStr);
      if (!isNaN(parseInt(numberStr))) {
        b.nRoadLanes = number;
      }
    }

    if (match("embedded_rails=tram")) {
      b.tramOrTrainRailsForward = true;
    } else if (match("embedded_rails:backward=tram")) {
      b.tramOrTrainRailsBackward = true;
    }

    if (key.substring(0, 8) === "sidewalk") {
      if (
        match("sidewalk=separate") ||
        match("sidewalk=both") ||
        match("sidewalk:both=separate") ||
        match("sidewalk:left=separate") ||
        match("sidewalk:left=yes")
      ) {
        b.left.sidewalk = true;
      }

      if (
        match("sidewalk=separate") ||
        match("sidewalk=both") ||
        match("sidewalk:both=separate") ||
        match("sidewalk:right=separate") ||
        match("sidewalk:right=yes")
      ) {
        b.right.sidewalk = true;
      }

      if (match("sidewalk=right")) {
        b.left.sidewalk = false;
        b.right.sidewalk = true;
      }

      if (match("sidewalk=left")) {
        b.left.sidewalk = true;
        b.right.sidewalk = false;
      }
    }

    if (key.substring(0, 8) === "cycleway") {
      if (match("cycleway=shared_lane")) {
        b.left.cycleLane = "shared";
        b.right.cycleLane = "shared";
      }

      if (match("cycleway:both=separate") || match("cycleway:right=separate")) {
        b.right.cycleLane = "separate";
      }
      if (match("cycleway:right=lane")) {
        b.right.cycleLane = "lane";
      }

      if (match("cycleway:both=separate") || match("cycleway:left=separate")) {
        b.left.cycleLane = "separate";
      }
      if (match("cycleway:left=lane")) {
        b.left.cycleLane = "lane";
      }

      if (match("cycleway=separate")) {
        b[b.frontSide].cycleLane = "separate";
      }
      if (match("cycleway=lane") || match("cycleway=share_busway")) {
        b[b.frontSide].cycleLane = "shared";
      }

      if (
        match("cycleway=opposite_lane") ||
        match("cycleway=opposite_share_busway")
      ) {
        b[b.backSide].cycleLane = "shared";
      }
    }

    if (key.substring(0, 7) === "parking") {
      if (
        (key === "parking:left" && value !== "no") ||
        (key === "parking:both" && value !== "no") ||
        (key === "parking:lane:left" && value !== "no") ||
        (key === "parking:lane:both" && value !== "no") ||
        match("parking=left")
      ) {
        b.left.parking = "parallel";
      }

      if (
        (key === "parking:right" && value !== "no") ||
        (key === "parking:both" && value !== "no") ||
        (key === "parking:lane:right" && value !== "no") ||
        (key === "parking:lane:both" && value !== "no") ||
        match("parking=right")
      ) {
        b.right.parking = "parallel";
      }

      if (
        (key === "parking:both:orientation" ||
          key === "parking:right:orientation") &&
        (value === "perpendicular" || value === "diagonal")
      ) {
        b.right.parking = "perpendicular";
      }
      if (
        (key === "parking:both:orientation" ||
          key === "parking:left:orientation") &&
        (value === "perpendicular" || value === "diagonal")
      ) {
        b.left.parking = "perpendicular";
      }
    }
  });
};
